import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EnumsService from '../../Services/enums.service';

/**
 * Fetches all data needed by the Account Cards Table page in parallel:
 * account cards, sort codes, pal codes (sorted by category orderNumber),
 * reserved card ids, and pal code prefixes.
 */
export const fetchAccountCardsTablePageData = createAsyncThunk(
    'accountCardsTablePage/fetchData',
    async (
        { caseId, accountantId, token, requestKey, backgroundRefresh },
        { rejectWithValue }
    ) => {
        try {
            const [
                accountCardsResponse,
                sortCodesResponse,
                palCodeCategoriesResponse,
                palCodesResponse,
                reservedResponse,
                prefixesResponse,
            ] = await Promise.all([
                EnumsService.GetAccountCardsByCaseId(caseId, token),
                EnumsService.GetSortCodesByAccountantId(accountantId, token),
                EnumsService.GetPalCodeCategoriesByAccountantId(accountantId, token),
                EnumsService.GetPalCodesByAccountantId(accountantId, token),
                EnumsService.GetReservedAccountCardIdsByCase(caseId, token),
                EnumsService.GetPalCodePrefixes(caseId, token),
            ]);

            const accountCardsData = accountCardsResponse.ok ? await accountCardsResponse.json() : [];
            const sortCodesData = sortCodesResponse.ok ? await sortCodesResponse.json() : [];
            const palCodeCategoriesData = palCodeCategoriesResponse.ok ? await palCodeCategoriesResponse.json() : [];
            const palCodesData = palCodesResponse.ok ? await palCodesResponse.json() : [];
            const reservedAccountCardIds = reservedResponse.ok ? await reservedResponse.json() : [];
            const palCodePrefixes = prefixesResponse.ok ? await prefixesResponse.json() : [];

            const palCodeOrderMap = {};
            (palCodeCategoriesData || []).forEach((category) => {
                palCodeOrderMap[category.palCodeCategoryId] = category.orderNumber || 9999;
            });

            const sortedPalCodes = [...(palCodesData || [])].sort((a, b) => {
                const orderA = palCodeOrderMap[a.palCodeId] || 9999;
                const orderB = palCodeOrderMap[b.palCodeId] || 9999;
                return orderA - orderB;
            });

            const palCodesOptions = sortedPalCodes.map((palCode) => ({
                key: palCode.palCodeId,
                value: palCode.palCodeId,
                label: palCode.palCodeId,
            }));

            const sortCodesOptions = (sortCodesData || []).map((sortCode) => ({
                key: sortCode.sortCodeId,
                value: sortCode.sortCodeId,
                label: `${sortCode.sortCodeId} - ${sortCode.sortCodeName} `,
            }));

            return {
                accountCardsData: Array.isArray(accountCardsData) ? accountCardsData : [],
                palCodesOptions,
                sortCodesOptions,
                reservedAccountCardIds: Array.isArray(reservedAccountCardIds) ? reservedAccountCardIds : [],
                palCodePrefixes: Array.isArray(palCodePrefixes) ? palCodePrefixes : [],
                requestKey,
                backgroundRefresh,
            };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    accountCardsData: [],
    palCodesOptions: [],
    sortCodesOptions: [],
    reservedAccountCardIds: [],
    palCodePrefixes: [],
    loading: false,
    error: null,
    lastFetchKey: null,
    expectedFetchKey: null,
};

const accountCardsTablePageSlice = createSlice({
    name: 'accountCardsTablePage',
    initialState,
    reducers: {
        resetAccountCardsTablePage: () => initialState,
        setAccountCardsData: (state, action) => {
            state.accountCardsData = action.payload;
        },
        setPalCodePrefixes: (state, action) => {
            state.palCodePrefixes = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAccountCardsTablePageData.pending, (state, action) => {
                const backgroundRefresh = action.meta?.arg?.backgroundRefresh;
                const hasExistingData = (state.accountCardsData?.length ?? 0) > 0;
                if (!(backgroundRefresh && hasExistingData)) state.loading = true;
                state.error = null;
                const key = action.meta?.arg?.requestKey;
                if (key != null) state.expectedFetchKey = key;
            })
            .addCase(fetchAccountCardsTablePageData.fulfilled, (state, action) => {
                const r = action.payload;
                const requestKey = r.requestKey;
                if (requestKey != null && state.expectedFetchKey != null && requestKey !== state.expectedFetchKey) {
                    state.loading = false;
                    return;
                }
                state.accountCardsData = r.accountCardsData;
                state.palCodesOptions = r.palCodesOptions;
                state.sortCodesOptions = r.sortCodesOptions;
                state.reservedAccountCardIds = r.reservedAccountCardIds;
                state.palCodePrefixes = r.palCodePrefixes;
                state.lastFetchKey = requestKey ?? state.lastFetchKey;
                state.loading = false;
            })
            .addCase(fetchAccountCardsTablePageData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const {
    resetAccountCardsTablePage,
    setAccountCardsData,
    setPalCodePrefixes,
} = accountCardsTablePageSlice.actions;

export default accountCardsTablePageSlice.reducer;
